import axios from 'axios';
import uuid from 'uuid';
import cookies from 'js-cookie';
import { select } from '../shared/utils/dom';

/**
 * Helper function.
 * Creates and inserts the API system unavailable error (if it's not
 * already in the document) and focuses the element
 */
export const showUnavailableError = () => {
  const container = select('#page-alerts');
  const notification = select('#js-online-features-unavailable');
  if (!container) {
    console.log('API error can not be shown because no element exists that matches the selector #page-alerts.');
  }

  if (notification) {
    notification.focus();
  } else {
    const html = `
      <div class="alert alert--error" id="js-online-features-unavailable" tabindex="-1" role="alert">
        <svg aria-hidden="true" class="icon icon-error " role="img">
          <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#icon-error"></use>
        </svg>
        <p><strong>Online Feature is Not Available</strong>
        <br>
        We're Sorry
         <br>
        This feature is currently unavailable. Please try again later.
        </p>
      </div>
    `;
    container.insertAdjacentHTML('afterbegin', html);
  }
};

/**
 * Helper function.
 * Removes the API unavailable message, if it's on the page.
 */
export const removeUnavailableError = () => {
  const container = select('#page-alerts');
  const notification = select('#js-online-features-unavailable');
  if (container && notification) {
    notification.parentNode.removeChild(notification);
  }
}

/**
 * Use the API.
 */
export class Api {
  /**
   * @param url The host of the api.
   */
  constructor(url) {
    this.url = url;
    this.globalConfig = {
      withCredentials: true,
      headers: {
        Accept: 'application/json',
      },
      xsrfCookieName: 'vlgXsrf',
      xsrfHeaderName: 'X-XSRF-TOKEN',
    };
  }

  getDiagId() { // eslint-disable-line class-methods-use-this
    const diagId = cookies.get('diagId');
    return diagId || uuid.v4();
  }

  /**
   * GET request using axios
   * @param {string} endpoint The endpoint for the resource
   * @param {object} params Query parameters
   * @param {boolean} showError whether to show the API unavailable error on error.
   */
  get(endpoint, params, showError = true) {
    // Add diagnostic ID to request parameters
    const diagId = this.getDiagId();
    params = {
      ...params,
      diagId,
    };

    const url = `${this.url}${endpoint}`;
    return axios.get(url, { params }, this.globalConfig)
      .catch((error) => {
        if (showError) {
          showUnavailableError();
        }
        return Promise.reject(error);
      })
      .then((response) => {
        removeUnavailableError();
        return Promise.resolve(response);
      })
  }

  post(endpoint, data, queryParams, showError = true) {
    const url = `${this.url}${endpoint}`;
    return this.postToUrl(url, data, queryParams, showError)
  }

  postToUrl(url, data, queryParams, showError = true) {
    // Add diagnostic ID to request parameters
    const diagId = this.getDiagId();
    queryParams = {
      ...queryParams,
      diagId,
    };
    const config = { ...this.globalConfig };
    config.params = queryParams;
    return axios.post(url, data, config)
      .catch((error) => {
        if (showError) {
          showUnavailableError();
        }
        return Promise.reject(error);
      })
      .then((response) => {
        removeUnavailableError();
        return Promise.resolve(response);
      })
  }

  put(endpoint, data, queryParams, showError = true) { // eslint-disable-line class-methods-use-this
    // Add diagnostic ID to request parameters
    const diagId = this.getDiagId();
    queryParams = {
      ...queryParams,
      diagId,
    };

    const config = { ...this.globalConfig };
    config.params = queryParams;

    const url = `${this.url}${endpoint}`; // eslint-disable-line no-undef
    return axios.put(url, data, config)
      .catch((error) => {
        if (showError) {
          showUnavailableError();
        }
        return Promise.reject(error);
      })
      .then((response) => {
        removeUnavailableError();
        return Promise.resolve(response);
      })
  }

  delete(endpoint, queryParams, showError = true) {
    // Add diagnostic ID to request parameters
    const diagId = this.getDiagId();
    queryParams = {
      ...queryParams,
      diagId,
    };

    const config = { ...this.globalConfig };
    config.params = queryParams;

    const url = `${this.url}${endpoint}`;
    return axios.delete(url, config)
      .catch((error) => {
        if (showError) {
          showUnavailableError();
        }
        return Promise.reject(error);
      })
      .then((response) => {
        removeUnavailableError();
        return Promise.resolve(response);
      })
  }
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/shared/api.js